// The owner report's team side: who looks after the salon, what they did in the month, what is set up, and the numbers
// the First month view leads with. Part of the payload in `owner-report-schema.ts`.
import { z } from 'zod';
import { count } from './owner-report-schema-primitives';

/** Who the report is from without an assigned team: the page reads "From your marketing team". */
export const OWNER_REPORT_PREPARED_BY = 'your marketing team';
/** Who the report is from once the salon has a named team: "From Alpha Media". */
export const OWNER_REPORT_PREPARED_BY_TEAM = 'Alpha Media';

/** How many people the report names. */
export const OWNER_REPORT_TEAM_MAX = 3;

export const OWNER_REPORT_TEAM_ROLES = ['ACCOUNT_MANAGER', 'SOCIAL_MEDIA'] as const;
export type OwnerReportTeamRole = (typeof OWNER_REPORT_TEAM_ROLES)[number];

/** A person the owner deals with. A name, a role and a photo: never an email, a phone or an id. */
export const ownerReportTeamMemberSchema = z.object({
  name: z.string(),
  role: z.enum(OWNER_REPORT_TEAM_ROLES),
  /** Our own stored photo; null shows initials. */
  avatarUrl: z.string().nullable(),
});
export type OwnerReportTeamMember = z.infer<typeof ownerReportTeamMemberSchema>;

/** What Hayven saw done in the month. A 0 stays 0 here: the page hides it. */
export const ownerReportWorkSchema = z.object({
  /** Comments that got a public reply or a DM: at most `social.comments`, which also counts skipped ones. */
  commentsAnswered: count,
  /** As on the social report. */
  dmsSent: count,
  /** Google reviews whose reply was written in the month, whenever the review was: not `google.reviews.answered`. */
  reviewsAnswered: count,
  googlePosts: count,
  /** Maps ranking scans finished in the month, one per search term. */
  rankChecks: count,
  /** Marketing texts, one per message: not people. */
  textsToClients: count,
  reviewRequests: count,
  /** Missed calls texted back, one per call. Not the Impact page's group, which also holds links texted to served callers. */
  missedCallTextBacks: count,
  /** Calls the AI answered, as on the Impact page. */
  callsAnswered: count,
});
export type OwnerReportWork = z.infer<typeof ownerReportWorkSchema>;

/** `REACH` = no counted guest in the month: the page leads with `reach` or the setup, never a big 0. */
export const OWNER_REPORT_HERO_MODES = ['GUESTS', 'REACH'] as const;
export type OwnerReportHeroMode = (typeof OWNER_REPORT_HERO_MODES)[number];

/** Signs of interest for a month with no counted guest: calls, directions and offers, never people. */
export const ownerReportReachSchema = z.object({
  /** `calls + directions + offers`, above 0. */
  total: count,
  /** From `google.profile`, only when its month is the report's: 0 while Google hasn't shared the month. */
  calls: count,
  directions: count,
  /** `ads.gotOffer`: offers and vouchers claimed online in the month. */
  offers: count,
});
export type OwnerReportReach = z.infer<typeof ownerReportReachSchema>;

/** What is running for the salon today, whichever month the report shows. */
export const ownerReportSetupFactsSchema = z.object({
  offerLive: z.boolean(),
  bioPagePublished: z.boolean(),
  /** Linked, and the connection behind the link still works. */
  metaAdsLinked: z.boolean(),
  googleAdsLinked: z.boolean(),
  /** Something answers comments, and the Facebook and Instagram connection works. */
  commentGiftOn: z.boolean(),
  /** Automatic review replies are on, and the Google link and listing can post them. */
  reviewRepliesOn: z.boolean(),
  /** A stand of this salon was tapped, at any time. */
  standInUse: z.boolean(),
});
export type OwnerReportSetupFacts = z.infer<typeof ownerReportSetupFactsSchema>;
